import React, { useState } from 'react';
import { TextField, Button, Container, Typography, Paper, Alert } from '@mui/material';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { requestPasswordReset } from '../services/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginTop: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: '0 3px 5px 2px rgba(0, 0, 0, .1)',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(2),
}));

function PasswordRecovery() {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);
        setIsSubmitting(true);

        try {
            await requestPasswordReset(email);
            setSuccess(true);
            // Clear the form
            setEmail('');
        } catch (error) {
            console.error('Password recovery request failed', error);
            if (error.response?.status === 429) {
                setError('Too many attempts. Please try again later.');
            } else if (error.response?.status === 404) {
                // We show a success message even if the email doesn't exist
                // This prevents email enumeration attacks
                setSuccess(true);
            } else {
                setError('An error occurred. Please try again later.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Container maxWidth="xs" style={{ marginTop: '50px' }}>
            <Typography variant="h4" component="h1" align="center" gutterBottom>
                Password Recovery
            </Typography>
            <StyledPaper elevation={3}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success ? (
                    <>
                        <Alert severity="success" sx={{ mb: 2 }}>
                            If an account exists with this email, you will receive password reset instructions shortly.
                        </Alert>
                        <StyledButton
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={() => navigate('/login')}
                        >
                            Return to Login
                        </StyledButton>
                    </>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <Typography variant="body2" color="textSecondary" paragraph>
                            Enter your email address and we'll send you instructions to reset your password.
                        </Typography>
                        <StyledTextField
                            variant="outlined"
                            required
                            fullWidth
                            label="Email Address"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={isSubmitting}
                        />
                        <StyledButton
                            type="submit"
                            fullWidth
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Sending...' : 'Reset Password'}
                        </StyledButton>
                        <StyledButton
                            fullWidth
                            variant="text"
                            onClick={() => navigate('/login')}
                            sx={{ mt: 1 }}
                        >
                            Back to Login
                        </StyledButton>
                    </form>
                )}
            </StyledPaper>
        </Container>
    );
}

export default PasswordRecovery;